import React, { useEffect, useRef, useState } from 'react';
import { useTenant } from '../TenantContext';
import { API_BASE_URL, ENTITY_NAMES, ATTRIBUTE_NAMES } from '../../constants';

interface EmailResultsModalProps {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly accentColor?: string;
    readonly backgroundColor?: string;
    readonly estimateId: string;
    readonly disclaimerText?: string;
    readonly noteText?: string;
}const isValidEmail = (email: string) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

export default function EmailResultsModal({ isOpen, onClose, accentColor, backgroundColor, estimateId, disclaimerText, noteText }: EmailResultsModalProps) {
    const { displayName, logoUrl, derivedAttributesData } = useTenant();
    const [email, setEmail] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const submitControllerRef = useRef<AbortController | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const modalRef = useRef<HTMLDialogElement | null>(null);
    const previousActiveElement = useRef<Element | null>(null);
    const [modalStyle, setModalStyle] = useState<React.CSSProperties | undefined>(undefined);

    const computePosition = () => {
        const viewportPadding = 16;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

        // desired modal width
        const measuredWidth = modalRef.current?.offsetWidth ?? 360;
        const modalWidth = Math.min(measuredWidth, Math.max(280, measuredWidth));

        // place modal below the header, centered horizontally
        const headerEl = document.querySelector('header');
        const headerHeight = headerEl?.offsetHeight ?? 80; // fallback to 80px if header not found
        const top = headerHeight + viewportPadding; // 16px below header
        const left = Math.max(viewportPadding, (viewportWidth - modalWidth) / 2);

        setModalStyle({
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            width: `${modalWidth}px`,
        });
    };
    useEffect(() => {
        if (!isOpen) return;

        previousActiveElement.current = document.activeElement;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // compute position after modal element renders
        setTimeout(computePosition, 10);
        // focus input
        setTimeout(() => inputRef.current?.focus(), 60);

        const onResize = () => computePosition();
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            document.body.style.overflow = prevOverflow;
            // restore focus on close
            (previousActiveElement.current as HTMLElement | null)?.focus?.();
            setModalStyle(undefined);
        };
    }, [isOpen]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        globalThis.addEventListener('keydown', onKey);
        return () => globalThis.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const handleSubmit = async (ev?: React.FormEvent) => {
        ev?.preventDefault();
        setError('');
        setSubmitError(null);

        if (!isValidEmail(email)) {
            setError('Please enter a valid email address.');
            inputRef.current?.focus();
            return;
        }

        const url = `https://netpriceestimate.com/api/estimates/${estimateId}/email-summary`;

        // Helper function to normalize logo URLs
        const normalizeLogoUrl = (url: string): string => {
            if (!url) return '';
            // If URL starts with './', remove it and prepend API_BASE_URL
            if (url.startsWith('./')) {
                return `${API_BASE_URL}/${url.substring(2)}`;
            }
            // If URL is already absolute (starts with http:// or https://), return as is
            if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
            }
            // Otherwise, prepend API_BASE_URL
            return `${API_BASE_URL}/${url}`;
        };

        // Get dynamic tenant data from API (via TenantContext) with localStorage fallback
        const tenantName = displayName ?? localStorage.getItem('TENANT_FULLNAME') ?? '';
        const rawTenantLogo = logoUrl ?? localStorage.getItem('TENANT_LOGO') ?? '';
        const tenantLogo = normalizeLogoUrl(rawTenantLogo);

        // Get footer logo with fallback chain
        let rawFooterLogo = localStorage.getItem('FOOTER_LOGO') ?? '';
        if (!rawFooterLogo) {
            try {
                const footerLinksMap = localStorage.getItem('FOOTER_LINKS_MAP');
                if (footerLinksMap) {
                    const parsed = JSON.parse(footerLinksMap);
                    rawFooterLogo = parsed?.Logo ?? '';
                }
            } catch (e) {
                console.error('Error parsing FOOTER_LINKS_MAP from localStorage:', e);
            }
        }
        // Normalize the footer logo URL
        let footerLogoUrl = normalizeLogoUrl(rawFooterLogo);
        // Final fallback: use tenant logo from API (already normalized)
        if (!footerLogoUrl) {
            footerLogoUrl = tenantLogo;
        }

        // Use disclaimerText from API or fallback to copyright text
        const footerText = disclaimerText;

        // Extract Connect With Us URL from Summary entity
        let connectUrl = '';
        try {
            const summaryObject = derivedAttributesData?.objects?.find((obj: any) => obj.name === ENTITY_NAMES.SUMMARY);
            if (summaryObject?.attributes) {
                const connectAttribute = summaryObject.attributes.find(
                    (attr: any) => attr.name === ATTRIBUTE_NAMES.CONNECT_WITH_US ||
                        attr.name?.toLowerCase().includes(ATTRIBUTE_NAMES.CONNECT_WITH_US.toLowerCase()) ||
                        attr.friendlyName?.toLowerCase().includes(ATTRIBUTE_NAMES.CONNECT_WITH_US.toLowerCase())
                );
                if (connectAttribute) {
                    const urlValue = connectAttribute.defaultValue ?? connectAttribute.constantValue;
                    if (urlValue) {
                        connectUrl = urlValue.trim();
                    }
                }
            }
        } catch (error) {
            console.error('Error extracting Connect With Us URL:', error);
        }

        const payload = {
            ToEmail: email,
            Subject: 'Your Estimate Summary',
            TenantName: tenantName,
            TenantLogo: tenantLogo,
            FooterLogo: footerLogoUrl,
            FooterText: footerText,
            ConnectUrl: connectUrl,
            primaryColor: '#000000',
            HeaderText: noteText,
        };

        const controller = new AbortController();
        submitControllerRef.current = controller;

        setIsSubmitting(true);

        try {
            const tenantName = localStorage.getItem('TENANT_NAME') ?? '';
            const normalizedTenantName = tenantName.trim().toLowerCase();
            const resp = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'authority-name': normalizedTenantName
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
                credentials: 'include',
            });


            if (!resp.ok) {
                let message = `Request failed: ${resp.status}`;
                try {
                    const json = await resp.json();
                    if (json?.message) message = json.message;
                } catch {
                    const text = await resp.text().catch(() => '');
                    if (text) message = text;
                }
                throw new Error(message);
            }

            // success
            await resp.json();
            setIsSubmitting(false);
            submitControllerRef.current = null;
            onClose();
        } catch (err: any) {
            if (err?.name === 'AbortError') {
                // aborted
                return;
            }
            setSubmitError(err?.message ?? 'Failed to send email. Please try again.');
            setIsSubmitting(false);
            submitControllerRef.current = null;
        }
    };

    // Abort any in-flight submit when modal closes or component unmounts
    useEffect(() => {
        if (!isOpen && submitControllerRef.current) {
            submitControllerRef.current.abort();
            submitControllerRef.current = null;
        }
        return () => {
            if (submitControllerRef.current) {
                submitControllerRef.current.abort();
                submitControllerRef.current = null;
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50" aria-hidden={!isOpen}>
            {/* overlay */}
            <div
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
                aria-hidden="true"
            />
            {/* modal box positioned at the top of the page */}
            <dialog
                ref={modalRef}
                open
                aria-labelledby="email-results-title"
                aria-describedby="email-results-desc"
                className="relative bg-white rounded-lg shadow-xl mx-4"
                style={{
                    zIndex: 60,
                    padding: 0,
                    ...(modalStyle || { width: 'min(90%,420px)', left: '50%', transform: 'translateX(-50%)', top: '96px', position: 'fixed' }), // fallback top: 80 + 16 = 96px
                }}
            >
                <button
                    onClick={onClose}
                    aria-label="Close email dialog"
                    className="absolute top-3 right-3 text-gray-600 hover:text-gray-800 focus:outline-none"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <div className="p-6 text-center">
                    <h2 id="email-results-title" className="text-lg font-bold mb-2">Email My Results</h2>
                    <p id="email-results-desc" className="text-sm text-gray-600 mb-4">
                        Enter your email below to receive your results (check your spam or junk folder if you don&apos;t see them in your inbox).
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div>
                            <label htmlFor="email-input" className="sr-only">Email</label>
                            <input
                                id="email-input"
                                ref={inputRef}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                aria-invalid={!!error}
                                aria-describedby={error ? 'email-error' : undefined}
                            />
                            {error && (
                                <p id="email-error" className="mt-1 text-sm text-red-600 text-left">{error}</p>
                            )}
                        </div>

                        <div>
                            <button
                                type="submit"
                                className="w-full py-2 rounded font-semibold"
                                style={{ backgroundColor: backgroundColor, color: accentColor ?? undefined }}
                                disabled={isSubmitting}
                                aria-busy={isSubmitting}
                                aria-disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Email NPC Estimate Results'}
                            </button>
                            {submitError && (
                                <p className="mt-2 text-sm text-red-600 text-left" role="alert">{submitError}</p>
                            )}
                        </div>
                    </form>
                </div>
            </dialog>
        </div>
    );
}
